import { IOperationResultNoValue, IUserPreferences } from '@ViewModels';
import { css } from 'aphrodite';
import { action, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	IUserSessionComponentProps,
	UserSessionViewModelKey,
} from '../../../../../models/AppState';
import { EventLogger, IEventLoggingComponentProps, withEventLogging } from '../../../../../models/Logging';
import {
	KeepInTouchCommitmentInterval,
	KeepInTouchCommitmentIntervalError,
	KeepInTouchCommitmentPreferencesViewModel,
	UserViewModel,
} from '../../../../../viewmodels/AppViewModels';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { RadioButton } from '../../../RadioButton';
import { styleSheet } from './styles';

interface IProps extends IErrorMessageComponentProps, IUserSessionComponentProps, IEventLoggingComponentProps {
	userViewModel: UserViewModel;
	onCancel?: () => void;
}

interface IState {
	selectedDigestOption?: [string, DigestNotificationInterval];
	selectedRecentMeetingsOption?: [string, boolean];
	selectedActionItemCalendarOption?: [string, boolean];
	showLoading?: boolean;
}

type DigestNotificationInterval = 'Never' | 'Daily' | 'Weekly';

const DigestRadioButtonOptions: [string, DigestNotificationInterval][] = [
	['Send me daily updates', 'Daily'],
	['Send me weekly updates', 'Weekly'],
	["I don't want to receive updates", 'Never'],
];

const RecentMeetingsRadioButtonOptions: [string, boolean][] = [
	['Show meetings with me as the only attendee', true],
	['Do not show meetings with me as the only attendee', false],
];

const ActionItemCalendarRadioButtonOptions: [string, boolean][] = [
	['Add action items to my calendar', true],
	['Do not add action items to my calendar', false],
];

class _EmailAndCalendarSettings extends React.Component<IProps, IState> {
	private mKeepInTouchCommitmentPreferences: KeepInTouchCommitmentPreferencesViewModel;
	public readonly state: IState = {};

	constructor(props: IProps) {
		super(props);

		makeObservable<_EmailAndCalendarSettings, 'mKeepInTouchCommitmentPreferences' | 'onResetChanges'>(this, {
			mKeepInTouchCommitmentPreferences: observable.ref,
			onResetChanges: action,
		});
	}

	public UNSAFE_componentWillMount() {
		const { userSession } = this.props;
		this.mKeepInTouchCommitmentPreferences = new KeepInTouchCommitmentPreferencesViewModel(
			userSession,

			userSession.user
		);
		this.onResetChanges();
	}

	public render() {
		const { userViewModel, userSession } = this.props;
		const { selectedDigestOption, selectedRecentMeetingsOption, selectedActionItemCalendarOption, showLoading } =
			this.state;

		const hasCalendaring = !!userSession.user.emailProviderFeatures.calendar.canViewHistoryAll;

		return (
			<>
				<div className='settings-group-radio-button-group'>
					<div className='settings-group-radio-button-group-header'>Email Settings</div>
					{DigestRadioButtonOptions.map((item, index) => {
						return (
							<RadioButton
								checked={selectedDigestOption === item}
								disabled={userViewModel.isBusy}
								id={`digest-email-settings-${index}`}
								key={index}
								name='digest-email-settings'
								onChange={this.onEmailDigestOptionChange(item)}
								value={item[1]}
							>
								{item[0]}
							</RadioButton>
						);
					})}
				</div>
				{!!hasCalendaring && (
					<div className='settings-group-radio-button-group'>
						<div className='settings-group-radio-button-group-header'>Recent Meetings</div>
						{RecentMeetingsRadioButtonOptions.map((item, index) => {
							return (
								<RadioButton
									checked={selectedRecentMeetingsOption === item}
									disabled={userViewModel.isBusy}
									id={`recent-meetings-settings-${index}`}
									key={index}
									name='recent-meetings-settings'
									onChange={this.onRecentMeetingOptionChanged(item)}
									value={`${item[1]}`}
								>
									{item[0]}
								</RadioButton>
							);
						})}
					</div>
				)}
				{!!hasCalendaring && (
					<div className='settings-group-radio-button-group'>
						<div className='settings-group-radio-button-group-header'>Action Items</div>
						{ActionItemCalendarRadioButtonOptions.map((item, index) => {
							return (
								<RadioButton
									checked={selectedActionItemCalendarOption === item}
									disabled={userViewModel.isBusy}
									id={`action-item-calendar-settings-${index}`}
									key={index}
									name='action-item-calendar-settings'
									onChange={this.onActionItemCalendarOptionChanged(item)}
									value={`${item[1]}`}
								>
									{item[0]}
								</RadioButton>
							);
						})}
					</div>
				)}
				{!!showLoading && !!userViewModel.isBusy && <LoadingSpinner className='absolute-center' type='large' />}
				<div className={css(styleSheet.buttonContainer)}>
					<button
						className={css(baseStyleSheet.ctaButton)}
						disabled={userViewModel.isBusy}
						onClick={this.onSaveClicked}
					>
						Save
					</button>
					<button
						className={css(baseStyleSheet.ctaButtonReverse)}
						disabled={userViewModel.isBusy}
						onClick={this.handleCancel}
					>
						Cancel
					</button>
				</div>
			</>
		);
	}

	private onSaveClicked = () => {
		const { userViewModel, errorMessages, userSession } = this.props;
		const { selectedDigestOption, selectedRecentMeetingsOption, selectedActionItemCalendarOption } = this.state;

		// validate
		// validate

		let errorMessage: string = null;
		switch (this.mKeepInTouchCommitmentPreferences.error) {
			case KeepInTouchCommitmentIntervalError.SelectAtLeasTwoDays: {
				errorMessage = 'You must select at least two days or choose "Weekly" instead.';
				break;
			}
			case KeepInTouchCommitmentIntervalError.SelectAtLeastOneDay: {
				errorMessage = 'You must select at least one day.';
				break;
			}
			case KeepInTouchCommitmentIntervalError.NeverNotSatisfied: {
				this.mKeepInTouchCommitmentPreferences.setInterval(KeepInTouchCommitmentInterval.Never);
				break;
			}
			default: {
				break;
			}
		}

		if (errorMessage) {
			errorMessages.push({
				messages: [errorMessage],
			});
			return;
		}

		const preferences: IUserPreferences = {
			digestNotificationInterval: selectedDigestOption[1],

			displayMeetingsWithoutOtherAttendees: selectedRecentMeetingsOption[1],
			keepInTouchCommitmentPreferences: this.mKeepInTouchCommitmentPreferences.toJs(),
			actionItemCalendarPreferences: {
				enabled: selectedActionItemCalendarOption?.[1],
			},
		};
		const promise = userViewModel.updateUserPreferences(preferences);
		if (promise) {
			this.setState({
				showLoading: true,
			});

			EventLogger.logEvent(
				{
					action: 'Save',
					category: 'EmailAndCalendarSettings',
				},
				{ ...preferences }
			);

			promise
				.then(prefs => {
					userSession.user.userPreferences = prefs;
					this.setState({
						showLoading: false,
					});

					toast.success('Changes were saved successfully!');
				})
				.catch((error: IOperationResultNoValue) => {
					EventLogger.logEvent(
						{
							action: 'Save-Error',
							category: 'EmailAndCalendarSettings',
						},
						{ ...error }
					);

					errorMessages.push({
						messages: [error.systemMessage],
					});
				});
		}
	};

	private onResetChanges = () => {
		const { userViewModel } = this.props;
		// Set the selected value for the dropdown
		let selectedValue: DigestNotificationInterval = 'Daily'; // Default to 1, which is daily notifications
		if (!!userViewModel.preferences && !!userViewModel.preferences.digestNotificationInterval) {
			selectedValue = userViewModel.preferences.digestNotificationInterval;
		}

		this.mKeepInTouchCommitmentPreferences.reset();
		this.setState({
			selectedDigestOption: DigestRadioButtonOptions.find(x => x[1] === selectedValue),
			selectedRecentMeetingsOption: RecentMeetingsRadioButtonOptions.find(
				x => x[1] === (userViewModel.preferences.displayMeetingsWithoutOtherAttendees || false)
			),
			selectedActionItemCalendarOption: ActionItemCalendarRadioButtonOptions.find(
				x => x[1] === (userViewModel.preferences.actionItemCalendarPreferences?.enabled ?? true)
			),
		});
	};

	private handleCancel = () => {
		this.onResetChanges();
		this.props.onCancel?.();
	};

	private onRecentMeetingOptionChanged =
		(selectedRecentMeetingsOption: [string, boolean]) => (e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				this.setState({
					selectedRecentMeetingsOption,
				});
			}
		};

	private onActionItemCalendarOptionChanged =
		(selectedActionItemCalendarOption: [string, boolean]) => (e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				this.setState({
					selectedActionItemCalendarOption,
				});
			}
		};

	private onEmailDigestOptionChange =
		(selectedDigestOption: [string, DigestNotificationInterval]) => (e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				this.setState({
					selectedDigestOption,
				});
			}
		};
}

const EmailAndCalendarSettingsAsObserver = observer(_EmailAndCalendarSettings);
const EmailAndCalendarSettingsWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(EmailAndCalendarSettingsAsObserver);
export const EmailAndCalendarSettings = withEventLogging(
	EmailAndCalendarSettingsWithContext,
	'EmailAndCalendarSettings'
);
